'use client';

import React, { useState, useEffect } from 'react';
import { discardHTMLTags, getCurrencySymbol, getBaseImageUrl, calculateOfferDuration } from '@/constants/hooks';
import { Offer } from '@/services/dataTypes';
import Image from 'next/image';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCalendarDays, faThumbsDown, faThumbsUp, faTimes, faCopy } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

interface Props {
    product: Offer;
    merchantName: string;
    merchantLogo: string;
    domain: string;
    isOpen: boolean;
    onClose: () => void;
}

const ProductDetailsModal = ({ product, merchantName, merchantLogo, domain, isOpen, onClose }: Props) => {
    const [mounted, setMounted] = useState(false);
    const [copied, setCopied] = useState(false);
    const [dealLoading, setDealLoading] = useState(false);
    const [hasLiked, setHasLiked] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setIsExpanded(false);
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        setCopied(false);
        setDealLoading(false);
        setHasLiked(false);
        setIsExpanded(false);
    }, [product?.unique_id]);

    const handleCopyCode = async () => {
        if (!product?.coupon_code) return;
        try {
            await navigator.clipboard.writeText(product.coupon_code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            try {
                const el = document.createElement('textarea');
                el.value = product.coupon_code;
                el.style.position = 'fixed';
                el.style.opacity = '0';
                const container = document.body || document.documentElement;
                if (container) {
                    container.appendChild(el);
                    el.focus();
                    el.select();
                    document.execCommand('copy');
                    container.removeChild(el);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                }
            } catch {
                toast.error('Failed to copy. Please copy manually.', { autoClose: 3000 });
            }
        }
    };

    const handleLikeStatus = () => {
        setHasLiked(true);
        toast.success("Thank you for your feedback!", { autoClose: 2000 });
    };

    const finalUrl = product?.url?.startsWith('/') ? product.url.replace(/^\/+/, '') : (product?.url ?? '');
    const absoluteOutUrl = `/${finalUrl}`;
    const imageSrc = (product?.offer_type?.name === 'product' && product?.product_image)
        ? getBaseImageUrl(domain, product.product_image, '')
        : getBaseImageUrl(domain, merchantLogo, '');

    const isLifetime = !product?.end_date || product?.end_date?.toLowerCase().includes('lifetime') || product?.end_date?.toLowerCase().includes('life time') || product?.end_date?.toLowerCase().includes('no expiry');

    const animationStyles = `
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes slideUp {
            from {
                opacity: 0;
                transform: translateY(24px) scale(0.98);
            }
            to {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }
    `;

    const modalContent = isOpen ? (
        <>
            <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
            <div
                className="fixed inset-0 z-[99999] flex items-center justify-center p-2 sm:p-5 overflow-y-auto"
                style={{ animation: 'fadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards', willChange: 'transform', isolation: 'isolate', transform: 'translateZ(0)' }}
            >
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-slate-950/50 transition-opacity duration-300"
                    onClick={onClose}
                />

                {/* Modal Window */}
                <div
                    className="relative bg-white rounded-2xl sm:rounded-[2.5rem] shadow-[0_25px_60px_-15px_rgba(79,70,229,0.2)] max-w-2xl w-full max-h-[92vh] sm:max-h-[85vh] overflow-hidden flex flex-col border border-slate-100 z-10"
                    style={{ animation: 'slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header Cover Banner */}
                    <div className="absolute top-0 left-0 right-0 h-36 bg-gradient-to-br from-indigo-50/80 via-indigo-50/20 to-transparent -z-10" />

                    {/* Close Trigger Button */}
                    <button
                        type="button"
                        aria-label="Close dialog"
                        onClick={onClose}
                        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-30 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white/95 hover:bg-white text-slate-500 hover:text-slate-900 border border-slate-100 hover:border-slate-200 transition-all duration-300 shadow-md hover:scale-105 active:scale-95"
                    >
                        <FontAwesomeIcon icon={faTimes} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>

                    {/* Header Area */}
                    <div className="flex-shrink-0 px-4 sm:px-8 pt-5 sm:pt-8 pb-3 sm:pb-4 relative">
                        <div className="flex items-start gap-3 sm:gap-5">
                            {/* Overlapping Brand Logo Container */}
                            {imageSrc && (
                                <div className="relative shrink-0 w-14 h-14 sm:w-20 sm:h-20 bg-white rounded-2xl sm:rounded-3xl p-2 sm:p-3 shadow-md border border-slate-50 flex items-center justify-center">
                                    <Image
                                        src={imageSrc}
                                        alt={merchantName}
                                        fill
                                        className="object-contain p-1.5 sm:p-2"
                                    />
                                </div>
                            )}

                            {/* Title & Badges */}
                            <div className="flex-1 min-w-0 pr-4 sm:pr-8">
                                {/* Badges row */}
                                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2 sm:mb-2.5">
                                    <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 bg-emerald-50 text-[9px] font-black uppercase tracking-widest text-emerald-700 rounded-md border border-emerald-100/50">
                                        <FontAwesomeIcon icon={faCheck} className="w-2.5 h-2.5" />
                                        Verified
                                    </span>
                                    {product?.end_date && !isLifetime && (
                                        <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 bg-amber-50 text-[9px] font-black uppercase tracking-widest text-amber-700 rounded-md border border-amber-100/50">
                                            <FontAwesomeIcon icon={faCalendarDays} className="w-2.5 h-2.5" />
                                            {calculateOfferDuration(product?.end_date)}
                                        </span>
                                    )}
                                </div>

                                <h2 className="text-base sm:text-xl font-extrabold text-slate-800 leading-snug line-clamp-2">
                                    {discardHTMLTags(product?.offer_title)}
                                </h2>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable Content Body */}
                    <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-3 sm:py-4 space-y-4 sm:space-y-6">

                        {/* Pricing Box */}
                        {(product?.sale_price || product?.original_price) && (
                            <div className="inline-flex items-center gap-2 sm:gap-2.5 px-3.5 sm:px-4 py-1.5 sm:py-2 bg-indigo-50/80 hover:bg-indigo-100/70 border border-indigo-100 hover:border-indigo-200 rounded-xl w-fit shadow-2xs transition-all duration-200 cursor-default">
                                <div className="flex items-baseline gap-2 sm:gap-3">
                                    {product?.sale_price && (
                                        <span className="text-xl sm:text-2xl font-black text-indigo-600 leading-none">
                                            {getCurrencySymbol(product?.currency)}{product?.sale_price}
                                        </span>
                                    )}
                                    {product?.original_price && (
                                        <span className="text-xs sm:text-base line-through text-slate-400 font-medium leading-none">
                                            {getCurrencySymbol(product?.currency)}{product?.original_price}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Coupon Code Section (if present) */}
                        {product?.coupon_code && (
                            <div className="bg-gradient-to-br from-indigo-50/40 via-indigo-50/10 to-transparent border border-dashed border-indigo-200/80 p-4 sm:p-5 rounded-2xl sm:rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                                <div className="text-center sm:text-left">
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                        Coupon Code
                                    </span>
                                    <p className="text-xl sm:text-2xl font-mono font-black text-indigo-600 tracking-wider mt-0.5 sm:mt-1 select-all">
                                        {product.coupon_code}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleCopyCode}
                                    className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-slate-900 hover:bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 shadow-md hover:shadow-indigo-500/25 active:scale-95 flex items-center justify-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faCopy} className="w-3.5 h-3.5" />
                                    {copied ? "Copied!" : "Copy Code"}
                                </button>
                            </div>
                        )}

                        {/* Offer Details/Terms */}
                        {product?.offer_detail && (() => {
                            const cleanDetail = discardHTMLTags(product.offer_detail);
                            const isLong = cleanDetail.length > 250;
                            return (
                                <div className="space-y-2 sm:space-y-2.5">
                                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                        Offer Details
                                    </h3>
                                    <div className="bg-slate-50/80 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-slate-100">
                                        <p className={`text-slate-600 text-xs sm:text-sm leading-relaxed text-justify ${isLong && !isExpanded ? 'line-clamp-2 sm:line-clamp-3' : ''
                                            }`}>
                                            {cleanDetail}
                                        </p>
                                        {isLong && (
                                            <button
                                                type="button"
                                                onClick={() => setIsExpanded(!isExpanded)}
                                                className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors cursor-pointer group"
                                            >
                                                <span>{isExpanded ? 'See Less' : 'See More'}</span>
                                                <svg
                                                    className={`w-3.5 h-3.5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Direct Shop Action Trigger */}
                        <button
                            type="button"
                            onClick={() => {
                                setDealLoading(true);
                                setTimeout(() => {
                                    window.open(absoluteOutUrl, '_blank', 'noopener,noreferrer');
                                    setDealLoading(false);
                                }, 800);
                            }}
                            disabled={dealLoading}
                            className="w-full sm:w-[80%] mx-auto h-12 sm:h-14 flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-black rounded-xl sm:rounded-2xl transition-all duration-300 shadow-lg hover:shadow-indigo-500/20 active:scale-95 text-xs uppercase tracking-widest"
                        >
                            {dealLoading ? (
                                <>
                                    <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                    Redirecting...
                                </>
                            ) : (
                                <>Go to Merchant Website</>
                            )}
                        </button>
                    </div>

                    {/* Footer Area */}
                    <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 flex-shrink-0">
                        <span className="text-[11px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">
                            Was this offer helpful?
                        </span>
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <button
                                type="button"
                                aria-label="Mark offer as helpful"
                                onClick={handleLikeStatus}
                                disabled={hasLiked}
                                className={`flex-1 sm:flex-none justify-center inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs font-extrabold transition-all duration-200 border cursor-pointer ${hasLiked
                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-md scale-105'
                                    : 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-500 hover:border-emerald-600 shadow-sm hover:shadow-md hover:shadow-emerald-500/25 hover:scale-[1.02] active:scale-95'
                                    }`}
                            >
                                <FontAwesomeIcon icon={faThumbsUp} className="w-3.5 h-3.5" />
                                Yes
                            </button>
                            <button
                                type="button"
                                aria-label="Mark offer as not helpful"
                                onClick={handleLikeStatus}
                                disabled={hasLiked}
                                className={`flex-1 sm:flex-none justify-center inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs font-extrabold transition-all duration-200 border cursor-pointer ${hasLiked
                                    ? 'bg-rose-600 text-white border-rose-600 shadow-md scale-105'
                                    : 'bg-rose-500 hover:bg-rose-600 text-white border-rose-500 hover:border-rose-600 shadow-sm hover:shadow-md hover:shadow-rose-500/25 hover:scale-[1.02] active:scale-95'
                                    }`}
                            >
                                <FontAwesomeIcon icon={faThumbsDown} className="w-3.5 h-3.5" />
                                No
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    ) : null;

    if (!mounted || typeof window === 'undefined' || !modalContent || !document?.body) return null;

    return createPortal(modalContent, document.body);
};

export default ProductDetailsModal;